import { css } from "@emotion/react";
import {
	AlertTriangle,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
} from "lucide-react";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button, buttonVariants } from "@/components/ui/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { AppConstants } from "../../data/AppConstants";
import { useAppConfigState } from "../../hooks/useAppConfigState";
import { type Catalog, useCatalogState } from "../../hooks/useCatalogState";
import { useCheckoutFormState } from "../../hooks/useCheckoutFormState";
import type { Product } from "../../hooks/useInventoryState";
import { useProductDetailsState } from "../../hooks/useProductDetailsState";
import {
	type ShoppingCartItemModel,
	useShoppingCartItemsState,
} from "../../hooks/useShoppingCartItemsState";
import { useStorefrontSettingsState } from "../../hooks/useStorefrontSettingsState";
import { useThemeState } from "../../hooks/useThemeState";
import { useTranslate } from "../../hooks/useTranslate/useTranslate";
import type { ImmutableArray, ImmutableObject } from "../../lib/immutable";
import { ReservationDates } from "../CheckoutForm/ReservationDates";
import { SelectLocation } from "../CheckoutForm/SelectLocation";
import { CustomScrollbars } from "../CustomScrollbars/CustomScrollbars";
import { ProductDetailsPopup } from "../ProductDetails/ProductDetails";
import { ShoppingCartItemRow } from "./Desktop/ShoppingCartItemDesktop";

//------------------------------------------------------------------------------------
export class ShoppingCartViewSettings {
	//hasImage: boolean = false;
	hasDepartmentCategorySubcategoryLinks: boolean = false;
	hasICode: boolean = false;
	//hasDescription: boolean = false;
	hasBarcodes: boolean = false;
	hasTags: boolean = false;
	hasDailyRate: boolean = false;
	hasWeeklyRate: boolean = false;
	hasMonthlyRate: boolean = false;
	hasSalesPrice: boolean = false;
	hasQty: boolean = false;
	hasRates: boolean = false;
	hasAvailability: boolean = false;
	hasDetailedDescription: boolean = false;
	hasTechnicalNotes: boolean = false;
	allowAddingUnavailableItems: boolean = false;
}

export const getShoppingCartCatalogViewSettings = (
	catalogs?: ImmutableArray<Catalog>,
	catalogId?: string,
): ShoppingCartViewSettings => {
	const viewSettings = new ShoppingCartViewSettings();
	if (catalogs && catalogId) {
		const catalog = catalogs.find((c) => c.Id === catalogId);
		if (catalog) {
			const shoppingCartFieldsByName = catalog.shoppingCartFieldsByName;
			if (shoppingCartFieldsByName) {
				//viewSttings.hasImage = (shoppingCartFieldsByName['IMAGE'] !== undefined) && (shoppingCartFieldsByName['IMAGE'].Selected);
				viewSettings.hasDepartmentCategorySubcategoryLinks =
					shoppingCartFieldsByName.DEPARTMENTCATEGORYSUBCATEGORYLINKS?.Selected;
				viewSettings.hasICode = shoppingCartFieldsByName.ICODE?.Selected;
				//viewSettings.hasDescription = (shoppingCartFieldsByName['DESCRIPTION'] !== undefined) && (shoppingCartFieldsByName['DESCRIPTION'].Selected);
				viewSettings.hasBarcodes =
					shoppingCartFieldsByName.BARCODENUMBER?.Selected;
				viewSettings.hasTags = shoppingCartFieldsByName.TAGS?.Selected;
				viewSettings.hasQty =
					shoppingCartFieldsByName.ITEMQUANTITYINPUT?.Selected;
				viewSettings.hasDailyRate =
					shoppingCartFieldsByName.DAILYRATE?.Selected;
				viewSettings.hasWeeklyRate =
					shoppingCartFieldsByName.WEEKLYRATE?.Selected;
				viewSettings.hasMonthlyRate =
					shoppingCartFieldsByName.MONTHLYRATE?.Selected;
				viewSettings.hasSalesPrice =
					shoppingCartFieldsByName.SALESPRICE?.Selected;
				viewSettings.hasRates =
					viewSettings.hasDailyRate ||
					viewSettings.hasWeeklyRate ||
					viewSettings.hasMonthlyRate;
				viewSettings.hasAvailability =
					shoppingCartFieldsByName.AVAILABILITY?.Selected;
				viewSettings.hasDetailedDescription =
					shoppingCartFieldsByName.DETAILEDDESCRIPTION?.Selected;
				viewSettings.hasTechnicalNotes =
					shoppingCartFieldsByName.TECHNICALNOTES?.Selected;
			}
			viewSettings.allowAddingUnavailableItems =
				catalog.AllowAddingUnavailableItems;
		}
	}
	return viewSettings;
};

export const getShoppingCartProductViewSettings = (
	catalogs?: ImmutableArray<Catalog>,
	product?: ImmutableObject<Product>,
): ShoppingCartViewSettings => {
	const catalogId = product?.WebCatalogId;
	const viewSettings = getShoppingCartCatalogViewSettings(catalogs, catalogId);
	if (product) {
		viewSettings.hasAvailability =
			viewSettings.hasAvailability && product.Availability !== undefined;
	}
	return viewSettings;
};

const ImportCartButton = () => {
	// Create a state variable to store the file content
	//const [fileContent, setFileContent] = useState('');
	const shoppingCartItemsState = useShoppingCartItemsState();
	const translate = useTranslate();

	// Define a function to handle the file input change event
	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		// Get the selected file from the input element
		if (e?.target?.files) {
			enqueueSnackbar(`Importing shopping cart...`, {
				variant: "info",
				autoHideDuration: 5000,
			});
			const file = e.target.files[0];
			const reader = new FileReader();
			reader.onload = (e: ProgressEvent<FileReader>) => {
				if (e?.target?.result && typeof e.target.result === "string") {
					const shoppingCartJson = e.target.result;
					//setFileContent(shoppingCartJson);
					const shoppingCartItems = JSON.parse(
						shoppingCartJson,
					) as ShoppingCartItemModel[];
					shoppingCartItemsState.replace(shoppingCartItems);
					//for (let i = 0; i < shoppingCartItems.length; i++) {
					//    const shoppingCartItem = shoppingCartItems[i];
					//    shoppingCartItemsState.add(shoppingCartItem);
					//}
					//localStorage.setItem('storefront-shoppingcartitems', shoppingCartJson);
					//shoppingCartState.
					//console.log(e.target.result);
				}
			};
			reader.readAsText(file);
		}
	};

	return (
		<div>
			<Button
				className="relative mx-2"
				variant="outline"
				onClick={async () => {}}
			>
				<input
					css={css`position:absolute;top:0;right:0;bottom:0;left:0;opacity:0;`}
					type="file"
					value=""
					onChange={handleFileChange}
				/>
				{translate("Import Cart")}
			</Button>

			{/*<pre>{fileContent}</pre>*/}
		</div>
	);
};

type ShoppingCartControlWebCatalog = {
	WebCatalogId: string;
	WebCatalog: string;
	TotalItems: number;
};
export const ShoppingCartControl = () => {
	const themeState = useThemeState();
	const themeName = themeState.get().themeName;
	const isDarkTheme = themeName === "dark";
	const borderColor = isDarkTheme ? "rgba(255,255,255,.1)" : "rgba(0,0,0,.1)";
	const shoppingCartItemsState = useShoppingCartItemsState();
	const navigate = useNavigate();
	const productDetailsState = useProductDetailsState();
	const checkoutFormState = useCheckoutFormState();
	const [mobileCartFiltersOpen, setMobileCartFiltersOpen] = useState(false);
	const [isDesktop, setIsDesktop] = useState<boolean>(() => {
		if (typeof window === "undefined") {
			return false;
		}
		return window.matchMedia("(min-width: 720px)").matches;
	});
	const catalogState = useCatalogState();
	const catalogs = catalogState.get().catalogs;
	const translate = useTranslate();
	const appConfigState = useAppConfigState();
	const checkoutUrl = appConfigState.getCheckoutUrl();

	const warehouseId = checkoutFormState.get().warehouseIdValue ?? "";
	const locationId = checkoutFormState.get().locationIdValue ?? "";
	const fromDate = checkoutFormState.get().fromDateValue ?? "";
	const toDate = checkoutFormState.get().toDateValue ?? "";
	const isLoadingCatalogs = catalogState.get().isLoadingCatalogs;
	const areCatalogsLoaded = catalogState.get().catalogs !== undefined;

	useEffect(() => {
		if (!isLoadingCatalogs && areCatalogsLoaded) {
			shoppingCartItemsState.reloadProductsAsync(
				warehouseId,
				locationId,
				fromDate,
				toDate,
			);
		}
	}, [
		isLoadingCatalogs,
		areCatalogsLoaded,
		warehouseId,
		locationId,
		fromDate,
		toDate,
		shoppingCartItemsState.reloadProductsAsync,
	]);

	const getIsRowNumberColumnVisible = (_catalogId?: string) => {
		const isVisible = true;
		return isVisible;
	};

	const getIsImageColumnVisible = (_catalogId?: string) => {
		const isVisible = true;
		return isVisible;
	};

	const getIsDescriptionColumnVisible = (_catalogId?: string) => {
		const isVisible = true;
		return isVisible;
	};

	const getIsPriceColumnVisible = (catalogId?: string) => {
		let isVisible = true;
		if (catalogId !== undefined) {
			const viewSettings = getShoppingCartCatalogViewSettings(
				catalogs,
				catalogId,
			);
			isVisible =
				viewSettings.hasDailyRate ||
				viewSettings.hasWeeklyRate ||
				viewSettings.hasMonthlyRate;
		}
		return isVisible;
	};

	const getIsQuantityColumnVisible = (_catalogId?: string) => {
		const isVisible = true;
		return isVisible;
	};

	const getTotalColumns = (catalogId?: string) => {
		const isRowNumberColumnVisible = getIsRowNumberColumnVisible(catalogId);
		const isImageColumnVisible = getIsImageColumnVisible(catalogId);
		const isDescriptionColumnVisible = getIsDescriptionColumnVisible(catalogId);
		const isPriceColumnVisible = getIsPriceColumnVisible(catalogId);
		const isQuantityColumnVisible = getIsQuantityColumnVisible(catalogId);
		let totalColumns = 0;
		if (isRowNumberColumnVisible) {
			totalColumns++;
		}
		if (isImageColumnVisible) {
			totalColumns++;
		}
		if (isDescriptionColumnVisible) {
			totalColumns++;
		}
		if (isPriceColumnVisible) {
			totalColumns++;
		}
		if (isQuantityColumnVisible) {
			totalColumns++;
		}
		return totalColumns;
	};

	const items = shoppingCartItemsState.get();
	let totalItemCount = 0;
	const webCatalogs: ShoppingCartControlWebCatalog[] = [];
	for (let i = 0; i < items.length; i++) {
		const item = items[i];
		if (item.qty > 0) {
			totalItemCount += item.qty;
			const webCatalog = webCatalogs.find(
				(c) => c.WebCatalogId === item.product.WebCatalogId,
			);
			if (item.product.WebCatalogId && item.product.WebCatalog && !webCatalog) {
				webCatalogs.push({
					WebCatalogId: item.product.WebCatalogId,
					WebCatalog: item.product.WebCatalog,
					TotalItems: item.qty,
				});
			} else if (webCatalog) {
				webCatalog.TotalItems += item.qty;
			}
		}
	}

	const storefrontSettingsState = useStorefrontSettingsState();
	const separateQuoteRequestsByCatalog =
		storefrontSettingsState.get().settings?.SeparateQuoteRequestsByCatalog ??
		false;
	const isUniversity =
		storefrontSettingsState.get().settings?.IsUniversity ?? false;
	const isDeveloper = localStorage.getItem("developer") === "true";
	//const locationSpecificShoppingCartItems: ShoppingCartItemModel[] = [];
	// if (!separateQuoteRequestsByCatalog) {
	// 	shoppingCartItemsState.get().forEach((item) => {
	// 		const webCatalog = catalogs?.find(
	// 			(c) => c.Id === item.product.WebCatalogId,
	// 		);
	// 		if (
	// 			webCatalog &&
	// 			webCatalog.WarehouseId === checkoutFormState.get().warehouseIdValue &&
	// 			webCatalog.LocationId === checkoutFormState.get().locationIdValue
	// 		) {
	// 			locationSpecificShoppingCartItems.push(item);
	// 		}
	// 	});
	// }
	// let locationSpecificItemCount = 0;
	// locationSpecificShoppingCartItems.forEach((item) => {
	// 	if (item.qty > 0) {
	// 		locationSpecificItemCount += item.qty;
	// 	}
	// });

	const [showClearCart, setShowClearCart] = useState(false);

	const closeClearCartDialog = () => {
		setShowClearCart(false);
	};

	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}
		const mediaQuery = window.matchMedia("(min-width: 720px)");
		const onChange = (event: MediaQueryListEvent) => {
			setIsDesktop(event.matches);
		};
		setIsDesktop(mediaQuery.matches);
		mediaQuery.addEventListener("change", onChange);
		return () => {
			mediaQuery.removeEventListener("change", onChange);
		};
	}, []);

	function download(filename: string, text: string) {
		var element = document.createElement("a");
		element.setAttribute(
			"href",
			`data:text/plain;charset=utf-8,${encodeURIComponent(text)}`,
		);
		element.setAttribute("download", filename);
		element.style.display = "none";
		document.body.appendChild(element);
		element.click();
		document.body.removeChild(element);
	}

	const exportCartAsync = async () => {
		const shoppingCartJson = localStorage.getItem(
			"storefront-shoppingcartitems",
		);
		if (typeof shoppingCartJson === "string") {
			download("shoppingcart.json", shoppingCartJson);
		}
	};

	const shoppingCartTableCss = css`
        transition: all .5s ease;
        margin:${separateQuoteRequestsByCatalog && webCatalogs.length > 1 ? "2rem 0 0 0" : "0"};
        width: 100%;
        border-collapse: collapse;
        & > thead {
            top: ${!separateQuoteRequestsByCatalog ? "-1px" : "unset"};
            z-index: ${!separateQuoteRequestsByCatalog ? "2" : "unset"};
            position: ${!separateQuoteRequestsByCatalog ? "sticky" : "unset"};
        }
        & > thead th {
            font-weight: normal;
            transition: all .5s ease;
            background-color: ${themeState.getTheme().palette.siteBackground};
        }
        & > thead tr.columnheaders th {
            background-color: #000000;
            color: #ffffff;
            font-size: .9rem;
        }
        & > tbody > tr.packageitem > td {
            padding: 0;
        }
        & > tfoot {
            transition: all .5s ease;
            border-top: 1px solid ${isDarkTheme ? "rgba(255,255,255,.1)" : "rgba(0,0,0,.1)"};
        }
    `;

	const shoppingCartTableThead = (catalogId?: string) => (
		<>
			<tr className="columnheaders">
				<th>{getIsRowNumberColumnVisible(catalogId) && null}</th>
				<th>{getIsImageColumnVisible(catalogId) && null}</th>
				<th css={css`text-align:left;padding:0 0 0 .25rem;`}>
					{getIsDescriptionColumnVisible(catalogId) && translate("Description")}
				</th>
				{isDesktop && (
					<th css={css`text-align:center;`}>
						{getIsPriceColumnVisible(catalogId) && <>{translate("Price")}</>}
					</th>
				)}
				<th>
					{getIsQuantityColumnVisible(catalogId) && translate("Quantity")}
				</th>
			</tr>
		</>
	);

	const footer = (catalog?: ShoppingCartControlWebCatalog) => (
		<>
			<div css={css`text-align:center;padding:4px 32px 0 0;`}>
				{!catalog && (
					<>
						{translate("Total Items")}: {totalItemCount}
					</>
				)}
				{catalog && (
					<>
						{translate("Total Items")}: {catalog.TotalItems}
					</>
				)}
			</div>
			<div
				css={css`flex:0 0 auto;padding:16px 0 16px 0;display:flex;align-items:center;justify-content:center;`}
			>
				<Button variant="outline" onClick={() => navigate(-1)}>
					<ChevronLeft />
					<span css={css`padding:0 .5rem;`}>{translate("Back")}</span>
				</Button>
				{isDeveloper && (
					<>
						<Button
							className="mx-2"
							variant="outline"
							disabled={shoppingCartItemsState.get().length === 0}
							onClick={async () => {
								return Promise.resolve().then(async () => {
									await exportCartAsync();
								});
							}}
						>
							{translate("Export Cart")}
						</Button>
						<ImportCartButton />
					</>
				)}
				<Button
					className="mx-2 whitespace-nowrap"
					variant="outline"
					disabled={shoppingCartItemsState.get().length === 0}
					onClick={async () => {
						return Promise.resolve().then(() => {
							setShowClearCart(true);
						});
					}}
				>
					{translate("Clear Cart")}
				</Button>
				<Button
					className="mx-2"
					variant="outline"
					disabled={shoppingCartItemsState.get().length === 0}
					onClick={async () => {
						return Promise.resolve().then(async () => {
							const warehouseid = checkoutFormState.get().warehouseIdValue;
							const locationid = checkoutFormState.get().locationIdValue;
							const fromDate = checkoutFormState.get().fromDateValue;
							const toDate = checkoutFormState.get().toDateValue;
							await shoppingCartItemsState.reloadProductsAsync(
								warehouseid,
								locationid,
								fromDate,
								toDate,
							);
							const hasMessages2 = getHasMessages();
							if (hasMessages2) {
								enqueueSnackbar(
									translate("Please resolve the errors in the cart."),
									{ variant: "error" },
								);
								return;
							}
							if (!separateQuoteRequestsByCatalog) {
								navigate(`/${checkoutUrl}`);
							} else {
								navigate(`/${checkoutUrl}/catalogid/${catalog?.WebCatalogId}`);
							}
						});
					}}
				>
					{translate("Submit")}...
					<ChevronRight />
				</Button>
			</div>
			{showClearCart && (
				<div
					css={css`display:flex;justify-content:center;padding-bottom:16px;`}
				>
					<Alert className="max-w-xs">
						<AlertTriangle className="text-amber-500" />
						<AlertTitle>{translate("Clear Cart?")}</AlertTitle>
						<AlertDescription>
							<div className="mt-2 flex items-center justify-center gap-2">
								<Button
									variant="outline"
									onClick={async () => {
										return Promise.resolve().then(() => {
											shoppingCartItemsState.reset(
												locationId,
												warehouseId,
												catalog?.WebCatalogId,
											);
											closeClearCartDialog();
										});
									}}
								>
									{translate("OK")}
								</Button>
								<Button
									variant="outline"
									onClick={async () => {
										return Promise.resolve().then(() => {
											closeClearCartDialog();
										});
									}}
								>
									{translate("Cancel")}
								</Button>
							</div>
						</AlertDescription>
					</Alert>
				</div>
			)}
		</>
	);

	useEffect(() => {
		if (productDetailsState.get().showPopup) {
			productDetailsState.setShowPopup(false);
		}
	}, [productDetailsState.get, productDetailsState.setShowPopup]);

	// clear out any shopping cart items for catalogs that are no longer accessible
	useEffect(() => {
		const allItemsInCart = shoppingCartItemsState.get();
		const removeList: ShoppingCartItemModel[] = [];
		const itemNames: string[] = [];
		if (catalogs) {
			for (let i = 0; i < allItemsInCart.length; i++) {
				const item = allItemsInCart[i];
				if (item?.product?.WebCatalogId) {
					const catalog = catalogs.find(
						(c) => c.Id === item.product.WebCatalogId,
					);
					if (!catalog) {
						itemNames.push(item.product.ItemName ?? "");
						removeList.push(item);
					}
				}
			}
		}
		shoppingCartItemsState.deleteItems(removeList);
		if (itemNames.length > 0) {
			enqueueSnackbar(
				`Some shopping cart items were removed because the catalog(s) are no longer available.`,
				{ variant: "info", persist: true },
			);
		}
	}, [
		catalogs,
		shoppingCartItemsState.deleteItems,
		shoppingCartItemsState.get,
	]);

	const getHasMessages = () => {
		const messages1 = shoppingCartItemsState.getMessages();
		let hasMessages1 = false;
		for (let i = 0; i < messages1.length; i++) {
			if (messages1[i].messages.length > 0) {
				hasMessages1 = true;
				break;
			}
		}
		return hasMessages1;
	};
	const messages = shoppingCartItemsState.getMessages();
	const hasMessages = getHasMessages();

	return (
		<div
			css={css`
            flex:1 1 0;
            display:flex;
            justify-content:center;
            `}
		>
			<div
				css={css`
                flex:1 1 0;
                max-width:${AppConstants.siteWidthCss};
                display:flex;
                flex-direction:column;
                overflow:hidden;
                min-width: 320px;
                contain: content; /* this is the container where you want fixed position popups to be relative to */
                @media (min-width: 720px) {
                    min-width: 512px;
                }`}
			>
				<div
					css={css`
                    flex: 0 0 auto;
                    text-align: center;
                    font-size: 2em;
                    font-weight: 600;
                    padding: .25em;
                `}
				>
					{translate("Shopping Cart")}
				</div>

				{!separateQuoteRequestsByCatalog && (
					<div
						css={css`border-bottom: 1px solid ${borderColor};padding:0 0 16px 0;display:flex;align-items:flex-start;justify-content:center;flex-wrap:wrap;`}
					>
						{isDesktop ? (
							<>
								{!isUniversity && (
									<span css={css`width:250px; margin:.5rem;`}>
										<SelectLocation disabled={false} />
									</span>
								)}
								<div>
									<div
										css={css`white-space:nowrap;display:flex;align-items:center;`}
									>
										<ReservationDates />{" "}
										<span css={css`padding:0 .5rem;`}></span>
									</div>
								</div>
							</>
						) : (
							<div css={css`width:min(100%, 34rem);padding:0 .75rem;`}>
								<Collapsible
									open={mobileCartFiltersOpen}
									onOpenChange={setMobileCartFiltersOpen}
								>
									<CollapsibleTrigger
										className={buttonVariants({
											variant: "outline",
											className: "w-full justify-between",
										})}
									>
										<span>Location & Dates</span>
										<ChevronDown
											size={16}
											className={
												mobileCartFiltersOpen
													? "rotate-180 transition-transform"
													: "transition-transform"
											}
										/>
									</CollapsibleTrigger>
									<CollapsibleContent>
										<div
											css={css`
												display:grid;
												gap:.75rem;
												padding-top:.75rem;
											`}
										>
											{!isUniversity && <SelectLocation disabled={false} />}
											<ReservationDates />
										</div>
									</CollapsibleContent>
								</Collapsible>
							</div>
						)}
					</div>
				)}
				<CustomScrollbars
					css={css`
                        flex: 1 1 0;
                    `}
				>
					{hasMessages && (
						<div
							css={css`padding:1rem;border-radius:13px;margin:1rem;border:2px solid #ffc107;`}
						>
							<div css={css`font-weight:bold;display:flex;align-items:center;`}>
								<AlertTriangle
									css={css`color:#ffc107;padding:0 .5rem;`}
									size={20}
								/>
								{translate("Important shopping cart messages")}:
							</div>
							{messages.map((message, index) => (
								// biome-ignore lint/suspicious/noArrayIndexKey: there doesn't seem to be a better option here
								<div key={index} css={css`padding: 1rem .5rem 0 .5rem;`}>
									<div css={css`font-size:.9rem;font-weight:bold;`}>
										{message.header}
									</div>
									<div css={css`font-size:.8rem;`}>{message.description}</div>
									<div css={css`font-size:.8rem;`}>
										<ul>
											{message.messages.map((item, index) => (
												// biome-ignore lint/suspicious/noArrayIndexKey: there doesn't seem to be a better option here
												<li key={index}>{item}</li>
											))}
										</ul>
									</div>
								</div>
							))}
						</div>
					)}
					{separateQuoteRequestsByCatalog &&
						webCatalogs.map((catalog, catalogIndex) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: don't want to change the logic right now
							<React.Fragment key={catalogIndex}>
								{/** biome-ignore lint/suspicious/noArrayIndexKey: don't want to change the logic right now */}
								<table key={catalogIndex} css={shoppingCartTableCss}>
									<thead>
										<tr>
											<th colSpan={getTotalColumns(catalog.WebCatalogId)}>
												<div
													css={css`display:flex;justify-content:center;align-items:center;padding:.25rem 0;`}
												>
													<div
														css={css`padding:0 16px 0 0;font-weight:bold;font-size:1.4rem;`}
													>
														- {catalog.WebCatalog} -
													</div>
												</div>
											</th>
										</tr>
										<tr>
											<th colSpan={getTotalColumns(catalog.WebCatalogId)}>
												<div
													css={css`border-bottom: 1px solid ${borderColor};padding:8px;display:flex;align-items:center;justify-content:center;flex-wrap:wrap;`}
												>
													<div
														css={css`display:flex;align-items:center;justify-content:center;flex-wrap:wrap;`}
													>
														{!separateQuoteRequestsByCatalog && (
															<SelectLocation disabled={false} />
														)}
														<ReservationDates
															catalogId={catalog.WebCatalogId}
														/>
													</div>
												</div>
											</th>
										</tr>
										{shoppingCartTableThead(catalog.WebCatalogId)}
									</thead>
									<tbody>
										{shoppingCartItemsState
											.get()
											.filter(
												(i) => i.product.WebCatalogId === catalog.WebCatalogId,
											)
											.map((shoppingCartItem, itemIndex) => (
												// biome-ignore lint/suspicious/noArrayIndexKey: don't want to change the logic here right now and risk breaking
												<React.Fragment key={itemIndex}>
													<ShoppingCartItemRow
														shoppingCartItem={shoppingCartItem}
														index={itemIndex}
														isDesktop={isDesktop}
														isRowNumberColumnVisible={getIsRowNumberColumnVisible(
															catalog.WebCatalogId,
														)}
														isImageColumnVisible={getIsImageColumnVisible(
															catalog.WebCatalogId,
														)}
														isDescriptionColumnVisible={getIsDescriptionColumnVisible(
															catalog.WebCatalogId,
														)}
														isPriceColumnVisible={getIsPriceColumnVisible(
															catalog.WebCatalogId,
														)}
														isQuantityColumnVisible={getIsQuantityColumnVisible(
															catalog.WebCatalogId,
														)}
													/>
												</React.Fragment>
											))}
									</tbody>
									<tfoot>
										<tr>
											<td colSpan={getTotalColumns(catalog.WebCatalogId)}>
												{footer(catalog)}
											</td>
										</tr>
									</tfoot>
								</table>
							</React.Fragment>
						))}

					{!separateQuoteRequestsByCatalog && (
						<table css={shoppingCartTableCss}>
							<thead>{shoppingCartTableThead()}</thead>
							<tbody>
								{items.map((shoppingCartItem, index) => (
									// biome-ignore lint/suspicious/noArrayIndexKey: don't want to change the logic right now
									<React.Fragment key={index}>
										<ShoppingCartItemRow
											shoppingCartItem={shoppingCartItem}
											index={index}
											isDesktop={isDesktop}
											isRowNumberColumnVisible={getIsRowNumberColumnVisible(
												shoppingCartItem.product.WebCatalogId,
											)}
											isImageColumnVisible={getIsImageColumnVisible(
												shoppingCartItem.product.WebCatalogId,
											)}
											isDescriptionColumnVisible={getIsDescriptionColumnVisible(
												shoppingCartItem.product.WebCatalogId,
											)}
											isPriceColumnVisible={getIsPriceColumnVisible(
												shoppingCartItem.product.WebCatalogId,
											)}
											isQuantityColumnVisible={getIsQuantityColumnVisible(
												shoppingCartItem.product.WebCatalogId,
											)}
										/>
									</React.Fragment>
								))}
							</tbody>
						</table>
					)}
				</CustomScrollbars>
				{!separateQuoteRequestsByCatalog && <>{footer()}</>}
				<ProductDetailsPopup
					layoutId={productDetailsState.get().product?.Id ?? "0"}
				/>
			</div>
		</div>
	);
};
